import { NodeSSH } from 'node-ssh'
import { DeepPartial } from 'utility-types'
import { string, array, func, object, boolean } from 'joiful'



export type dynamicArgs = { [key: string]: any }
export type deployFunction = (deployConfig: DeployConfig, environmentConfig: EnvironmentConfig, serverConfig: ServersConfig, dynamicArgs: dynamicArgs, ssh: NodeSSH) => Promise<void>
export type healthCheckFunction = (ssh: NodeSSH, port: number) => Promise<void>
export type setupDynamicArgsFunction = (deployConfig: DeployConfig, environmentConfig: EnvironmentConfig, serverConfig: ServersConfig, dynamicArgs: dynamicArgs, ssh: NodeSSH) => Promise<void>
export type deployAction = 'default' | 'stop' | 'revert'
export type createTagFunction = (isSemverTag: boolean, isVersionedTag: boolean, releaseNumber: number, semverVersion: string | undefined, environmentName: string, branch: string | undefined) => string
export type parseTagFunction = (isSemverTag: boolean, isVersionedTag: boolean, allTags: string[], environmentName: string, branch: string | undefined) => { semverVersion: string | undefined, releaseNumber: number | undefined }
export type createBaseConfigFunction = (buildTemplate: Json, deployTemplate: Json, stopTemplate: Json, stages: Json, revertTemplate?: Json) => Json

export type JsonPrimitive = string | number | boolean | null | undefined
export type JsonMap = { [key: string]: JsonPrimitive | JsonArray | JsonMap }
export type JsonArray = (JsonPrimitive | JsonArray | JsonMap)[]
export type Json = JsonPrimitive | JsonMap | JsonArray

export class SshConfig {
  @(string().required())
  host: string

  @(string().required())
  username: string

  @(string().required())
  privateKey: string
}

export class UploadConfig {
  @(array().items(joi => joi.string()).required())
  files: string[]

  @(array().items(joi => joi.string()).required())
  folders: string[]

  @(string().required())
  uploadTo: string
}

export class ServersConfig {
  // TODO generic type DeployFunction<returnType>
  @(func().optional())
  getPort?: (deployConfig: DeployConfig, environmentConfig: EnvironmentConfig, serverConfig: ServersConfig, dynamicArgs: dynamicArgs, ssh: NodeSSH) => Promise<number>

  @(func().required())
  getAppName: (deployConfig: DeployConfig, environmentConfig: EnvironmentConfig, serverConfig: ServersConfig, dynamicArgs: dynamicArgs, ssh: NodeSSH) => Promise<string>

  @(object().required())
  sshConfig: SshConfig
}

export class EnvironmentConfig {
  @(string().required())
  environmentName: string


  @(func().required())
  environmentTagCreate: createTagFunction

  @(func().required())
  environmentTagParseVersion: parseTagFunction

  @(boolean().required())
  environmentTagIsSemver: boolean

  @(boolean().required())
  environmentTagIsVersioned: boolean

  @(boolean().required())
  deployManual: boolean

  @(string().optional())
  defaultEnvironmentBranch?: string

  @(boolean().required())
  isMultiEnv: boolean

  // Keep the second-latest build on the server after every deploy and
  // generate a manual revert_<env> gitlab job that switches back to it
  @(boolean().optional())
  revertible?: boolean

  @(object().optional())
  envVars?: { [key: string]: string }

  @(object().required())
  uploadConfig: UploadConfig

  @(func().optional())
  setupDynamicArgs?: setupDynamicArgsFunction

  @(func().optional())
  deployBefore?: deployFunction

  @(func().optional())
  deployUpload?: deployFunction

  @(func().optional())
  deployStart?: deployFunction

  @(func().optional())
  deployAfter?: deployFunction

  @(func().optional())
  onStop?: deployFunction

  @(func().optional())
  onRevert?: deployFunction

  @(func().optional())
  healthCheck?: healthCheckFunction

  @(array({ elementClass: ServersConfig }).required())
  servers: ServersConfig[]

  @(string().optional())
  auto_stop_in?: string
}

export class DeployConfig {
  @(array({ elementClass: EnvironmentConfig }).required())
  environments: EnvironmentConfig[]

  @(func().optional())
  setupDynamicArgs?: setupDynamicArgsFunction

  @(array().items(joi => joi.string()).required())
  requiredEnvVars: string[]

  @(func().optional())
  deployBefore?: deployFunction

  @(func().required())
  deployUpload: deployFunction

  @(func().required())
  deployStart: deployFunction

  @(func().optional())
  deployAfter?: deployFunction

  @(func().required())
  healthCheck: healthCheckFunction

  @(func().required())
  onStop: deployFunction

  @(func().required())
  onRevert: deployFunction

  @(func().optional())
  createBaseConfig?: createBaseConfigFunction
}

export class CiScripts {
  @(array().items(joi => joi.string()).required())
  before: string[]

  @(array().items(joi => joi.string()).required())
  main: string[]

  @(array().items(joi => joi.string()).required())
  after: string[]
}


export class CiConfig {
  @(object().required())
  deploy: CiScripts

  @(object().required())
  build: CiScripts

  @(object().required())
  stop: CiScripts

  @(object().required())
  revert: CiScripts
}

export type CiStage = keyof CiConfig
export type CiPhase = keyof CiScripts
export type PartialCiConfig = DeepPartial<CiConfig>
export type PartialDeployConfig = DeepPartial<DeployConfig>

// TODO remove TEMP example
export const ciConfig: CiConfig = {
  deploy: {
    before: [],
    main: [
      'echo "Starting deploy"',
      'yarn ci:deploy',
    ],
    after: []

  },
  build: {
    before: [],
    main: [
      // Run unit tests
      'echo "Running tests"',
      'yarn ci:test',

      // Run build
      'echo "Building project"',
      'yarn ci:build',

      // Run build health check,
      'echo "Checking build"',
      'yarn ci:check-build',
    ],
    after: []
  },
  stop: {
    before: [],
    main: [
      // Run unit tests
      'echo "Stopping environment"',
    ],
    after: []
  },
  revert: {
    before: [],
    main: [
      'echo "Reverting environment"',
    ],
    after: []
  }
}

// TODO remove TEMP example
export const deployConfig: DeployConfig = {
  requiredEnvVars: ['CI_COMMIT_SHORT_SHA', 'CI_TOOLS_COMMIT_TIMESTAMP', 'CI_COMMIT_TAG'],
  onStop: async () => { },
  onRevert: async () => { },
  deployUpload: async () => { },
  deployStart: async () => { },
  healthCheck: async () => { },
  environments: [
    {
      environmentName: 'production',
      environmentTagCreate: () => { return 'tag' },
      environmentTagIsSemver: true,
      environmentTagIsVersioned: true,
      isMultiEnv: false,
      deployManual: true,
      environmentTagParseVersion: () => { return { releaseNumber: 1, semverVersion: '1.1.1' } },
      envVars: {
        APP_ENV: 'production'
      },
      uploadConfig: {
        files: ['ecosystem.production.config.js', '.frackrc.js'],
        folders: ['build'],
        uploadTo: 'www',
      },
      servers: [{
        getPort: async () => 8080,
        getAppName: async () => 'my-app',
        sshConfig: {
          host: 'my-production-server.com',
          username: 'deploy',
          privateKey: process.env.KEY_MASTER,
        }
      }]
    }
  ]
}